import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { compressImage } from '@/shared/utils/compressImage'
import { createInvoice, parseInvoiceFile, uploadInvoiceFile } from '../api/invoicesService'
import type {
  CreateInvoiceRequest,
  Invoice,
  ParsedInvoiceResponse,
} from '../schemas/invoicesSchema'

export const BULK_INVOICE_SOFT_LIMIT = 50

export type ParseStatus = 'pending' | 'parsing' | 'parsed' | 'failed'
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'discarded' | 'failed'

export interface QueueItem {
  id: string
  file: File
  parseStatus: ParseStatus
  parsed?: ParsedInvoiceResponse | undefined
  saveStatus: SaveStatus
  savedInvoiceId?: string | undefined
  parseError?: string | undefined
  saveError?: string | undefined
}

export interface UseBulkInvoiceQueueResult {
  items: QueueItem[]
  currentIndex: number
  current: QueueItem | undefined
  isOpen: boolean
  enqueue: (files: File[]) => Promise<void>
  close: () => void
  next: () => void
  prev: () => void
  goTo: (id: string) => void
  saveCurrent: (values: CreateInvoiceRequest) => Promise<Invoice | null>
  discardCurrent: () => void
  retryParseCurrent: () => Promise<void>
  hasUnfinished: boolean
}

const PARSE_CONCURRENCY = 3

export function useBulkInvoiceQueue(): UseBulkInvoiceQueueResult {
  const [items, setItems] = useState<QueueItem[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)

  const qc = useQueryClient()

  const itemsRef = useRef<QueueItem[]>([])
  itemsRef.current = items

  const inFlightRef = useRef(0)
  const queueRef = useRef<string[]>([])
  const pumpRef = useRef<() => void>(() => {})

  const parseOne = useCallback(async (itemId: string, file: File): Promise<void> => {
    setItems(prev =>
      prev.map(it => (it.id === itemId ? { ...it, parseStatus: 'parsing' } : it)),
    )
    try {
      const parsed = await parseInvoiceFile(file)
      setItems(prev =>
        prev.map(it =>
          it.id === itemId
            ? { ...it, parseStatus: 'parsed', parsed, parseError: undefined }
            : it,
        ),
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Parse failed'
      setItems(prev =>
        prev.map(it =>
          it.id === itemId
            ? { ...it, parseStatus: 'failed', parseError: msg }
            : it,
        ),
      )
    } finally {
      inFlightRef.current -= 1
      pumpRef.current()
    }
  }, [])

  const pump = useCallback((): void => {
    while (inFlightRef.current < PARSE_CONCURRENCY && queueRef.current.length > 0) {
      const itemId = queueRef.current.shift()!
      const item = itemsRef.current.find(i => i.id === itemId)
      if (!item) continue
      inFlightRef.current += 1
      void parseOne(itemId, item.file)
    }
  }, [parseOne])

  pumpRef.current = pump

  const enqueue = useCallback(async (files: File[]): Promise<void> => {
    const processed = await Promise.all(files.map(f => compressImage(f)))
    const newItems: QueueItem[] = processed.map(file => ({
      id: crypto.randomUUID(),
      file,
      parseStatus: 'pending',
      saveStatus: 'idle',
    }))
    const wasEmpty = items.length === 0
    // Update itemsRef eagerly so pump() can find the new items immediately.
    itemsRef.current = [...itemsRef.current, ...newItems]
    setItems(itemsRef.current)
    if (wasEmpty) setCurrentIndex(0)
    setIsOpen(true)
    queueRef.current.push(...newItems.map(i => i.id))
    pump()
  }, [items.length, pump])

  const close = useCallback((): void => {
    setIsOpen(false)
    setItems([])
    setCurrentIndex(0)
    queueRef.current = []
    inFlightRef.current = 0
  }, [])

  const current = items[currentIndex]
  const hasUnfinished = items.some(
    i =>
      (i.parseStatus === 'parsed' || i.parseStatus === 'failed') &&
      i.saveStatus !== 'saved' &&
      i.saveStatus !== 'discarded',
  )

  const next = useCallback((): void => {
    setCurrentIndex(i => {
      const last = Math.max(0, items.length - 1)
      return Math.min(i + 1, last)
    })
  }, [items.length])

  const prev = useCallback((): void => {
    setCurrentIndex(i => Math.max(0, i - 1))
  }, [])

  const goTo = useCallback((id: string): void => {
    const idx = items.findIndex(it => it.id === id)
    if (idx >= 0) setCurrentIndex(idx)
  }, [items])

  function findNextUnfinishedIndex(list: QueueItem[], startFrom: number): number {
    for (let i = startFrom; i < list.length; i++) {
      const s = list[i]!.saveStatus
      if (s !== 'saved' && s !== 'discarded') return i
    }
    return -1
  }

  const advanceAfter = useCallback((list: QueueItem[], itemId: string): void => {
    const idx = list.findIndex(it => it.id === itemId)
    const startFrom = idx + 1
    const nextIdx = findNextUnfinishedIndex(list, startFrom)
    if (nextIdx === -1) {
      const fromStart = findNextUnfinishedIndex(list, 0)
      if (fromStart === -1) {
        // Whole queue is finished — reset state so the next drop starts a
        // fresh modal instead of appending to the already-saved items.
        setIsOpen(false)
        setItems([])
        setCurrentIndex(0)
        itemsRef.current = []
        queueRef.current = []
        inFlightRef.current = 0
      } else {
        setCurrentIndex(fromStart)
      }
    } else {
      setCurrentIndex(nextIdx)
    }
  }, [])

  const saveCurrent = useCallback(
    async (values: CreateInvoiceRequest): Promise<Invoice | null> => {
      const item = itemsRef.current[currentIndex]
      if (!item) return null

      setItems(prev =>
        prev.map(it =>
          it.id === item.id ? { ...it, saveStatus: 'saving', saveError: undefined } : it,
        ),
      )

      let invoice: Invoice
      try {
        invoice = await createInvoice(values)
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Save failed'
        setItems(prev =>
          prev.map(it =>
            it.id === item.id ? { ...it, saveStatus: 'failed', saveError: msg } : it,
          ),
        )
        return null
      }

      try {
        await uploadInvoiceFile(invoice.uuid, item.file)
      } catch {
        toast.warning('Invoice created but file upload failed. You can re-upload later.')
      }

      const nextList = itemsRef.current.map(it =>
        it.id === item.id
          ? { ...it, saveStatus: 'saved' as const, savedInvoiceId: invoice.uuid }
          : it,
      )
      setItems(nextList)
      itemsRef.current = nextList
      void qc.invalidateQueries({ queryKey: ['invoices'] })
      advanceAfter(nextList, item.id)
      return invoice
    },
    [advanceAfter, currentIndex, qc],
  )

  const discardCurrent = useCallback((): void => {
    const item = itemsRef.current[currentIndex]
    if (!item) return
    const nextList = itemsRef.current.map(it =>
      it.id === item.id ? { ...it, saveStatus: 'discarded' as const } : it,
    )
    setItems(nextList)
    itemsRef.current = nextList
    advanceAfter(nextList, item.id)
  }, [advanceAfter, currentIndex])

  const retryParseCurrent = useCallback(async (): Promise<void> => {
    const item = itemsRef.current[currentIndex]
    if (!item) return
    // Eagerly mirror the reset to itemsRef so pump() can find the item.
    const nextList = itemsRef.current.map(it =>
      it.id === item.id
        ? { ...it, parseStatus: 'pending' as const, parseError: undefined }
        : it,
    )
    setItems(nextList)
    itemsRef.current = nextList
    queueRef.current.push(item.id)
    pump()
  }, [currentIndex, pump])

  return {
    items,
    currentIndex,
    current,
    isOpen,
    enqueue,
    close,
    next,
    prev,
    goTo,
    saveCurrent,
    discardCurrent,
    retryParseCurrent,
    hasUnfinished,
  }
}
